import {
  type ExtractedClaim,
  type FactCheckClaim,
  type FactCheckSourceRecord,
  type StoredFactCheck,
  correctionRestatesClaim,
  quoteIsInExcerpt,
} from './article-factcheck';

/**
 * Re-running the claim check on identical article text flipped verdicts: the 2026-10-03
 * ITC re-run turned `contradicted` into `unverifiable` and moved one article from
 * `failed` to `needs_review`, which the release gate lets through. The judge is a
 * model; sampling at temperature 0 narrows that variance but does not remove it, so
 * the rule that matters is in code.
 *
 * A wrong-law verdict (`contradicted` / `misapplied`) rests on a quoted passage of the
 * statute. A later run on the SAME text cannot quietly retire it:
 *
 * - If the later run also says wrong law, the later verdict stands.
 * - If the later run says `supported` AND has a verified quote for it (`resolveFactCheckClaims`
 *   only ever returns `supported` with a quote found in the fetched text), it stands, and
 *   the claim is flagged as disputed.
 * - Otherwise (`unverifiable` / `unsupported`, i.e. no verified quote for a pass) the earlier
 *   wrong-law verdict is kept, flagged as disputed, provided its quote is still in the text
 *   fetched now. A quote the law no longer contains is not evidence for keeping it.
 */
export const DISPUTED_BETWEEN_RUNS = 'Disputed between runs';

const isWrongLaw = (claim: Pick<FactCheckClaim, 'verdict'>) =>
  claim.verdict === 'contradicted' || claim.verdict === 'misapplied';

const sentence = (verdict: FactCheckClaim['verdict']) =>
  verdict === 'contradicted' ? 'contradicted by the law it cites' : 'applied beyond its scope';

export interface StabilityOutcome {
  claims: FactCheckClaim[];
  /** Earlier wrong-law verdicts kept because the later run had no verified quote against them. */
  held: number;
  /** Earlier wrong-law verdicts retired by a later `supported` with a verified quote. */
  overturned: number;
}

export function holdPriorWrongLaw(input: {
  claims: readonly FactCheckClaim[];
  extracted: readonly ExtractedClaim[];
  sources: readonly FactCheckSourceRecord[];
  /** The stored check for this exact article text, or null. Only a CURRENT one counts. */
  prior: StoredFactCheck | null;
}): StabilityOutcome {
  const { prior } = input;
  if (!prior?.isCurrent) return { claims: [...input.claims], held: 0, overturned: 0 };
  const earlier = new Map(prior.report.claims.map((claim) => [claim.id, claim] as const));
  const refsOf = new Map(input.extracted.map((claim) => [claim.id, claim.refs] as const));
  const sources = new Map(input.sources.map((source) => [source.sourceId, source] as const));
  let held = 0;
  let overturned = 0;
  const claims = input.claims.map((claim): FactCheckClaim => {
    const before = earlier.get(claim.id);
    if (!before || before.text !== claim.text || !isWrongLaw(before) || !before.quote.trim())
      return claim;
    // An earlier `contradicted` whose correction restates the sentence's own figure was never a finding.
    if (
      before.verdict === 'contradicted' &&
      correctionRestatesClaim(before.text, before.correction)
    )
      return claim;
    if (isWrongLaw(claim)) return claim;
    if (claim.verdict === 'supported' && claim.quote.trim()) {
      overturned += 1;
      return {
        ...claim,
        disputed: true,
        note: `${DISPUTED_BETWEEN_RUNS}: an earlier check of this exact text found this sentence ${sentence(before.verdict)}; this run found it supported by a quoted passage.`,
      };
    }
    const stillInTheLaw = (refsOf.get(claim.id) ?? []).some((ref) => {
      const source = sources.get(ref.sourceId);
      return Boolean(
        source?.fetchedOk && source.excerpt && quoteIsInExcerpt(before.quote, source.excerpt),
      );
    });
    if (!stillInTheLaw) return claim;
    held += 1;
    return {
      ...claim,
      verdict: before.verdict,
      quote: before.quote,
      correction: before.correction,
      applies: before.applies,
      reason: before.reason,
      sourceUrl: before.sourceUrl ?? claim.sourceUrl,
      disputed: true,
      note: `${DISPUTED_BETWEEN_RUNS}: an earlier check of this exact text found this sentence ${sentence(before.verdict)}, and this run could not back a different answer with a quoted passage. The earlier verdict is kept.`,
    };
  });
  return { claims, held, overturned };
}

/** One line for the record's limitations, or null when no claim was disputed. */
export function disputeLimitation(outcome: Pick<StabilityOutcome, 'held' | 'overturned'>) {
  const parts = [
    outcome.held
      ? `${outcome.held} claim${outcome.held === 1 ? '' : 's'} kept ${outcome.held === 1 ? 'its' : 'their'} earlier wrong-law verdict`
      : null,
    outcome.overturned
      ? `${outcome.overturned} earlier wrong-law verdict${outcome.overturned === 1 ? ' was' : 's were'} retired by a quoted passage`
      : null,
  ].filter(Boolean);
  return parts.length
    ? `Two runs of this check on the same article text disagreed: ${parts.join('; ')}. A later run cannot clear a wrong-law verdict without a verified quote; editing the article starts a fresh check.`
    : null;
}
